import { Dispatch, useState } from "react";
import { HStack, Text, Button } from "@chakra-ui/react";
import { Annotation, useAnnotator } from "@annotorious/react";
import { useNavigate } from "react-router-dom";

import { useDeleteObservation } from "@/hooks";
import { Alert } from "@/components/ui/alert";
import { ConfirmationDialog } from "@/components";
import { DeviceOfflineError } from "@/errors";

interface IFormActionButtons {
  nurseryId: string;
  tankId?: string;
  rackId?: string;
  observationId?: string;
  note: string;
  setNote: Dispatch<React.SetStateAction<string>>;
  createError?: Error | null;
  createIsPending: boolean;
  updateMode: boolean;
  editMode: boolean;
  setEditMode: Dispatch<React.SetStateAction<boolean>>;
  annotationsCount: number;
  savedAnnotations: Annotation[];
}

function FormActionButtons ({
  nurseryId,
  tankId,
  rackId,
  observationId,
  note,
  setNote,
  createError,
  createIsPending,
  updateMode,
  editMode,
  setEditMode,
  savedAnnotations,
  annotationsCount
}: IFormActionButtons) {
  const anno = useAnnotator();
  const navigate = useNavigate();
  const { deleteObservation, deleteIsPending } = useDeleteObservation(nurseryId!, rackId!, observationId!);
  const [savedNote] = useState<string>(note);
  const handleCancel = () => {
    navigate(`/nurseries/${nurseryId}/tanks/${tankId || ""}/`);
  };
  return (
    <>
      {createError && (
        <>
          {createError instanceof DeviceOfflineError ? (
            <Alert mt="4" status="warning" title="Your device is offline">
              <Text mb="4">
              We will upload the observation, once the device is back
              online.
              </Text>
              <Button onClick={handleCancel}>Ok</Button>
            </Alert>
          ) : (
            <Alert mt="4" status="error" title={createError.message} />
          )}
        </>
      )}
      {editMode && (
        <HStack gap="4" mt="4">
          <ConfirmationDialog
            confirmationFn={
              updateMode
                ? () => {
                  setEditMode(false);
                  anno.setAnnotations(structuredClone(savedAnnotations), true);
                  setNote(savedNote);
                }
                : handleCancel
            }
            dialogTitle={updateMode ? "Discard observation edits?" : "Cancel and discard observation?"}
            dialogText={updateMode
              ? "Any changes you have made will not be saved."
              : "The photo and its annotations will not be saved."}
            cancelButtonText={updateMode ? "Continue editing" : "Continue observation"}
            confirmButtonText={updateMode ? "Discard edits" : "Discard observation"}
            isAlert
            trigger={
              <Button
                variant="outline"
                flex="1"
                size={["xs", null, "md"]}
              >
                Cancel
              </Button>
            }
          />
          <Button
            type="submit"
            size={["xs", null, "md"]}
            flex="1"
            colorPalette="blue"
            disabled={!rackId}
            loading={createIsPending}
            loadingText="Saving..."
          >
            Save
          </Button>
        </HStack>
      )}
      {updateMode && (
        <HStack gap="4" mt="4">
          <ConfirmationDialog
            confirmationFn={() => {
              deleteObservation({
                onSuccess: handleCancel,
              });
            }}
            dialogTitle="Delete observation?"
            dialogText={
              <>
                <strong>{annotationsCount} fragments</strong> and their
              associated labels will be removed.
              </>
            }
            confirmationIsPending={deleteIsPending}
            isPendingText="Deleting..."
            isAlert
            confirmButtonText="Delete"
            trigger={
              <Button variant="outline" flex="1" size="xs" colorPalette="red">
              Delete Observation
              </Button>
            }
          />
        </HStack>
      )}
    </>
  );
}

export default FormActionButtons;
